import { Module, VuexModule, Action } from "vuex-class-modules";
import store from "@/store/store";
import {
  GiftCardStatus,
  IGenerateGiftCardBatchDTO,
  IGiftCardBatchDTO,
  IGiftCardDTO,
  IGiftCardTypeDTO,
  IRedeemGiftCardRequestDTO,
  ISellGiftCardsRequestDTO,
  IUpsertGiftCardTypeDTO,
} from "@shared/types";
import { GiftCardService } from "@/services/api/GiftCardService";
import { snackbarModule } from "@/store/modules/snackbar/snackbarModule";
import { i18n } from "@/main";

@Module({ generateMutationSetters: true })
class GiftCardsModule extends VuexModule {
  private service = GiftCardService.getInstance();

  public loading = false;
  public types: IGiftCardTypeDTO[] = [];
  public batches: IGiftCardBatchDTO[] = [];
  public cards: IGiftCardDTO[] = [];

  public async ensureInitialized(): Promise<void> {
    if (!this.types.length) {
      await this.fetchAllData();
    }
  }

  @Action
  async fetchAllData(): Promise<void> {
    this.loading = true;
    try {
      const [types, batches, cards] = await Promise.all([
        this.service.getTypes(),
        this.service.getBatches({ includeCards: 'false' }),
        this.service.getGiftCards({}),
      ]);
      this.types = types;
      this.batches = batches;
      this.cards = cards;
    } catch (error) {
      console.error(error);
      snackbarModule.postErrorSnackbarMessage("Kunne ikke hente gavekortdata.");
    } finally {
      this.loading = false;
    }
  }

  @Action
  async fetchTypes(): Promise<void> {
    this.loading = true;
    try {
      this.types = await this.service.getTypes();
    } catch (error) {
      console.error(error);
      snackbarModule.postErrorSnackbarMessage("Kunne ikke hente gavekorttyper.");
    } finally {
      this.loading = false;
    }
  }

  @Action
  async saveType(dto: IUpsertGiftCardTypeDTO): Promise<IGiftCardTypeDTO | undefined> {
    this.loading = true;
    try {
      const result = await this.service.upsertType(dto);
      snackbarModule.postSuccessSnackbarMessage("Gavekorttypen er lagret.");
      await this.fetchTypes();
      return result;
    } catch (error) {
      console.error(error);
      snackbarModule.postErrorSnackbarMessage("Kunne ikke lagre gavekorttype.");
      return undefined;
    } finally {
      this.loading = false;
    }
  }

  @Action
  async generateBatch(dto: IGenerateGiftCardBatchDTO): Promise<IGiftCardBatchDTO | undefined> {
    this.loading = true;
    try {
      const batch = await this.service.generate(dto);
      snackbarModule.postSuccessSnackbarMessage("Gavekort er generert.");
      await this.fetchBatches();
      await this.fetchCards();
      return batch;
    } catch (error) {
      console.error(error);
      snackbarModule.postErrorSnackbarMessage("Kunne ikke generere gavekort.");
      return undefined;
    } finally {
      this.loading = false;
    }
  }

  @Action
  async fetchBatches(): Promise<void> {
    this.loading = true;
    try {
      this.batches = await this.service.getBatches({ includeCards: 'false' });
    } catch (error) {
      console.error(error);
      snackbarModule.postErrorSnackbarMessage("Kunne ikke hente batcher.");
    } finally {
      this.loading = false;
    }
  }

  @Action
  async fetchCards(status?: GiftCardStatus): Promise<void> {
    this.loading = true;
    try {
      this.cards = await this.service.getGiftCards(
        status !== undefined ? { status } : {}
      );
    } catch (error) {
      console.error(error);
      snackbarModule.postErrorSnackbarMessage("Kunne ikke hente gavekort.");
    } finally {
      this.loading = false;
    }
  }

  @Action
  async exportBatch(options: { batchId: number; suppressSnackbar?: boolean }): Promise<boolean> {
    const { batchId, suppressSnackbar = false } = options;
    try {
      const blob = await this.service.exportBatchPdf({ batchId });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `giftcards-batch-${batchId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      if (!suppressSnackbar) {
        snackbarModule.postSuccessSnackbarMessage("PDF generert.");
      }
      return true;
    } catch (error) {
      console.error(error);
      snackbarModule.postErrorSnackbarMessage("Kunne ikke eksportere batch.");
      return false;
    }
  }

  @Action
  async sellGiftCards(dto: ISellGiftCardsRequestDTO): Promise<IGiftCardDTO[] | undefined> {
    if (!dto.codes || dto.codes.length === 0) {
      return [];
    }
    try {
      const result = await this.service.sell(dto);
      snackbarModule.postSuccessSnackbarMessage("Gavekort er aktivert.");
      await this.fetchCards();
      return result;
    } catch (error) {
      console.error(error);
      snackbarModule.postErrorSnackbarMessage("Kunne ikke aktivere gavekort.");
      return undefined;
    }
  }

  /**
   * Redeems a gift card. Posts the success snackbar itself, but rethrows on failure so the
   * caller can show the server's message (see getApiErrorMessage) or its own fallback.
   */
  @Action
  async redeemGiftCard(dto: IRedeemGiftCardRequestDTO): Promise<IGiftCardDTO> {
    const result = await this.service.redeem(dto);
    snackbarModule.postSuccessSnackbarMessage(i18n.t("giftCardDialogs.redeemed").toString());
    try {
      await this.fetchCards();
    } catch (error) {
      // fetchCards reports its own errors; the redemption itself succeeded.
      console.error(error);
    }
    return result;
  }

  @Action
  async lookupByCode(code: string): Promise<IGiftCardDTO | null> {
    try {
      const trimmed = code.trim();
      if (!trimmed) return null;
      const result = await this.service.getByCode(trimmed);
      return result || null;
    } catch (error) {
      console.error(error);
      snackbarModule.postErrorSnackbarMessage(i18n.t("giftCardDialogs.notFound").toString());
      return null;
    }
  }
}

export const giftCardsModule = new GiftCardsModule({ store, name: "giftCardsModule" });
